/**
 * Breadcrumbs component for navigation and SEO
 * Provides visual navigation trail and structured data
 */

'use client';

import Link from 'next/link';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

export default function Breadcrumbs({ items, className = '' }: BreadcrumbsProps) {
  // Always include home as first item
  const allItems: BreadcrumbItem[] = [
    { name: 'Home', url: '/' },
    ...items,
  ];

  return (
    <nav
      aria-label="Breadcrumb"
      className={`mb-6 ${className}`}
    >
      <ol className="flex items-center flex-wrap gap-2 text-sm text-foreground-secondary">
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          const isHome = index === 0;

          return (
            <li key={item.url} className="flex items-center gap-2">
              {index > 0 && (
                <ChevronRight className="w-4 h-4 text-foreground-muted" aria-hidden="true" />
              )}
              {isLast ? (
                <span
                  className="text-foreground font-medium"
                  aria-current="page"
                >
                  {isHome && <Home className="w-4 h-4 inline mr-1" aria-hidden="true" />}
                  {item.name}
                </span>
              ) : (
                <Link
                  href={item.url}
                  className="text-foreground-secondary hover:text-accent transition-colors"
                >
                  {isHome && <Home className="w-4 h-4 inline mr-1" aria-hidden="true" />}
                  {!isHome && item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
